// components/VisionProductScanner.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Card, CardContent, Typography, LinearProgress, Button } from "@mui/material";
import { GoogleGenerativeAI } from "@google/generative-ai";

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GOOGLE_SEARCH_API_KEY = process.env.REACT_APP_GOOGLE_SEARCH_API_KEY;
const GOOGLE_SEARCH_CX = process.env.REACT_APP_GOOGLE_SEARCH_CX;

/**
 * Robust helper to kill camera hardware immediately.
 */
const releaseCamera = (videoEl, streamRef) => {
  const stopTracks = (stream) => {
    if (stream && stream.getTracks) {
      stream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
    }
  };

  if (streamRef?.current) {
    stopTracks(streamRef.current);
    streamRef.current = null;
  }

  if (videoEl?.srcObject) {
    stopTracks(videoEl.srcObject);
    videoEl.srcObject = null;
  }

  if (videoEl) {
    videoEl.pause();
    videoEl.removeAttribute("src");
    videoEl.load();
  }
};

const parseProductInfo = (text) => {
  const getField = (regex) => {
    const match = text.match(regex);
    return match ? match[1].trim() : "";
  };

  return {
    barcode: getField(/Barcode:\s*(.+)/i) !== "Not detected" ? getField(/Barcode:\s*(.+)/i) : "",
    name: getField(/Product Name:\s*(.+)/i),
    price: getField(/Estimated Price:\s*(.+)/i),
    category: getField(/Category:\s*(.+)/i),
    description: getField(/Description:\s*(.+)/i),
    brand: getField(/Brand:\s*(.+)/i),
    capacity: getField(/Capacity:\s*(.+)/i) || getField(/Size:\s*(.+)/i),
  };
};

const fetchProductImage = async (productName) => {
  if (!productName || productName === "Not available") return null;
  try {
    const searchQuery = encodeURIComponent(productName);
    const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${searchQuery}&searchType=image&key=${GOOGLE_SEARCH_API_KEY}&cx=${GOOGLE_SEARCH_CX}&num=5`;
    const response = await fetch(apiUrl);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.items && data.items.length > 0) {
      const amazonImage = data.items.find(item => 
        item.displayLink?.includes("amazon") || item.link?.includes("amazon")
      );
      return amazonImage ? amazonImage.link : data.items[0].link;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export default function VisionProductScanner({ 
  active = true, 
  onDetected, 
  onError, 
  onCaptureRef, 
  hideInternalButton = false 
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const hasDetectedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [statusText, setStatusText] = useState("Initializing camera...");

  const stopCameraHardware = useCallback(() => {
    releaseCamera(videoRef.current, streamRef);
  }, []);

  const identifyProduct = useCallback(async (base64Image) => {
    if (!GEMINI_API_KEY) throw new Error("Gemini API Key is missing.");
    const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
    const model = genAI.getGenerativeModel({ model: "gemini-3-flash-preview" });

    // RESTORED YOUR ORIGINAL PROMPT
    const prompt = `
     Analyze the provided image (which contains a barcode).

Perform the following steps:

1. Analyze the provided image and detect whether a barcode is present.

2. If a barcode is detected, read and report the barcode number.

3. Using the detected barcode number, attempt to identify the associated product from known databases or public references.

4. If a product is identified, retrieve its product details.

Strictly format your response as follows (use "Not available" if information cannot be determined):

Barcode: [Detected Barcode Number or "Not detected"]
Product Name: [Exact Product Name or "Not available"]
Brand: [Brand Name or "Not available"]
Estimated Price: [Price with currency or "Not available"]
Category: [Product Category or "Not available"]
Capacity: [Size/Weight/Volume or "Not available"]
Description: [1–2 sentence description or "Not available"]
    `;

    const result = await model.generateContent([
      { inlineData: { mimeType: "image/jpeg", data: base64Image } },
      { text: prompt }
    ]);

    const response = await result.response;
    return parseProductInfo(response.text() || "No information found.");
  }, []);

  const analyzeFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || hasDetectedRef.current || scanning) return;

    setScanning(true);
    setStatusText("Capturing...");
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/jpeg", 0.8);
      const base64Image = imageData.replace(/^data:image\/\w+;base64,/, "");

      // KILL CAMERA IMMEDIATELY after capture is saved to canvas
      hasDetectedRef.current = true;
      stopCameraHardware(); 
      
      setStatusText("Identifying with Gemini AI...");
      const productData = await identifyProduct(base64Image);

      setStatusText("Fetching product image...");
      let imageUrl = null;
      if (productData.name && productData.name !== "Not available") {
        imageUrl = await fetchProductImage(productData.name);
      }

      const formattedData = {
        name: productData.name !== "Not available" ? productData.name : "",
        brand: productData.brand !== "Not available" ? productData.brand : "",
        category: productData.category !== "Not available" ? productData.category : "",
        description: productData.description !== "Not available" ? productData.description : "",
        price: productData.price !== "Not available" ? productData.price : "",
        barcode: productData.barcode || "",
        capacity: productData.capacity !== "Not available" ? productData.capacity : "",
        images: imageUrl ? [imageUrl] : [],
      };

      onDetected?.(formattedData);
      setStatusText("Success!");
    } catch (err) {
      setError(err.message || "Failed to identify product");
      setStatusText("Error identifying product.");
      hasDetectedRef.current = false; 
      onError?.(err.message);
    } finally {
      setScanning(false);
    }
  }, [scanning, stopCameraHardware, identifyProduct, onDetected, onError]);

  const startCamera = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      hasDetectedRef.current = false;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setLoading(false);
      setStatusText("Ready to capture");
    } catch (err) {
      setError("Camera access denied.");
      setLoading(false);
    }
  }, []);

  const handleCapture = useCallback(() => {
    analyzeFrame();
  }, [analyzeFrame]);

  useEffect(() => {
    if (onCaptureRef) onCaptureRef.current = handleCapture;
  }, [handleCapture, onCaptureRef]);

  useEffect(() => {
    if (active) startCamera();
    else stopCameraHardware();
    return () => stopCameraHardware();
  }, [active, startCamera, stopCameraHardware]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Card sx={{ width: "100%", maxWidth: 500, borderRadius: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Product Scanner</Typography>

          {(loading || scanning) && <LinearProgress sx={{ mb: 2 }} />}
          
          <Box sx={{ position: "relative", width: "100%", borderRadius: 2, overflow: "hidden", bgcolor: "#000", minHeight: 250 }}>
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              sx={{
                width: "100%",
                display: (scanning || hasDetectedRef.current) ? "none" : "block",
              }}
            />
            
            {(scanning || hasDetectedRef.current) && (
              <Box sx={{ p: 6, textAlign: "center", color: "white" }}>
                <Typography>Processing with AI...</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>Camera sensor deactivated</Typography>
              </Box>
            )}

            {!scanning && !hasDetectedRef.current && (
              <Box sx={{
                position: "absolute", top: "50%", left: "50%", width: 240, height: 240,
                transform: "translate(-50%, -50%)", border: "2px dashed #2196f3",
                borderRadius: "12px", boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)", pointerEvents: "none"
              }} />
            )}
          </Box>

          <canvas ref={canvasRef} style={{ display: "none" }} />

          <Typography sx={{ mt: 2, textAlign: "center", fontSize: "14px", color: "text.secondary" }}>
            {statusText}
          </Typography>

          {!hideInternalButton && !hasDetectedRef.current && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleCapture}
                disabled={loading || scanning}
                sx={{ px: 4, borderRadius: 2 }}
              >
                Capture & Identify
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}